/**
 * Pure helpers for schedule toolbar filters: option lists from visible blocks and
 * block-level match tests for work-scope and worker filters.
 */

import { getWorkerDisplayLabel } from "../../../../utils/business/worker";
import { itemMatchesSearchQuery } from "../../../../utils/common/searchFilter";
import { getScheduleAspectForBlockOnDate } from "./scheduleBlockSelectors";
import { getScheduleBlockRowId } from "./scheduleRowModel";

/**
 * @typedef {{ value: string, label: string }} ScheduleFilterOption
 */

/**
 * @param {Object} worker
 * @returns {string}
 */
function getWorkerFilterLabel(worker) {
  return getWorkerDisplayLabel(worker);
}

/**
 * @param {Object} worker
 * @returns {string}
 */
function getWorkerFilterValue(worker) {
  return worker?.id != null ? String(worker.id) : "";
}

/**
 * Unique work scopes on blocks, sorted by label.
 *
 * @param {Array<Object>} blocks
 * @returns {ScheduleFilterOption[]}
 */
export function collectWorkScopeFilterOptions(blocks) {
  const byId = new Map();
  for (const block of blocks ?? []) {
    const id = block?.scope?.id;
    if (id == null) {
      continue;
    }
    const key = String(id);
    if (byId.has(key)) {
      continue;
    }
    const label = block.scope?.name?.trim() || key;
    byId.set(key, { value: key, label });
  }
  return [...byId.values()].sort((a, b) =>
    a.label.localeCompare(b.label, undefined, { sensitivity: "base" })
  );
}

/**
 * Map visible projects to filter options, sorted by project-number label.
 *
 * @param {Array<Object>} projects
 * @returns {ScheduleFilterOption[]}
 */
export function collectProjectFilterOptions(projects) {
  return (projects ?? [])
    .map((project) => ({
      value: String(project.id),
      label: `${project.projectNumber} — ${project.name}`,
    }))
    .sort((a, b) =>
      a.label.localeCompare(b.label, undefined, { numeric: true })
    );
}

/**
 * Unique workers on blocks and in-range per-day aspects (includes override crews).
 *
 * @param {Array<Object>} blocks
 * @param {Array<Object>} aspects
 * @param {Array<Date>} visibleDays
 * @returns {ScheduleFilterOption[]}
 */
export function collectWorkerFilterOptions(blocks, aspects, visibleDays) {
  const byId = new Map();

  const addWorker = (worker) => {
    const value = getWorkerFilterValue(worker);
    if (!value || byId.has(value)) {
      return;
    }
    byId.set(value, { value, label: getWorkerFilterLabel(worker) });
  };

  for (const block of blocks ?? []) {
    for (const day of visibleDays ?? []) {
      const aspect = getScheduleAspectForBlockOnDate(aspects, block.id, day);
      const assignment = aspect
        ? aspect.worker_assignment ?? []
        : block.worker_assignment ?? [];
      for (const worker of assignment) {
        addWorker(worker);
      }
    }
  }

  return [...byId.values()].sort((a, b) =>
    a.label.localeCompare(b.label, undefined, { sensitivity: "base" })
  );
}

/**
 * @param {Object} block
 * @param {Array<string>} scopeIds
 * @returns {boolean}
 */
export function blockMatchesWorkScopeFilter(block, scopeIds) {
  if (!scopeIds?.length) {
    return true;
  }
  return scopeIds.includes(String(block?.scope?.id));
}

/**
 * @param {Object} block
 * @param {Array<Object>} aspects
 * @param {Array<Date>} visibleDays
 * @param {Array<string>} workerIds
 * @returns {boolean}
 */
export function blockMatchesWorkerFilter(block, aspects, visibleDays, workerIds) {
  if (!workerIds?.length) {
    return true;
  }

  for (const day of visibleDays ?? []) {
    if (countSelectedWorkersOnScheduleBlockDay(block, aspects, day, workerIds) > 0) {
      return true;
    }
  }

  return false;
}

/**
 * Count how many of the selected workers are assigned on one block/day, using the
 * per-day aspect crew when present and falling back to the block-level default crew.
 *
 * @param {Object} block
 * @param {Array<Object>} aspects
 * @param {Date|null|undefined} day
 * @param {Array<string>} workerIds
 * @returns {number}
 */
export function countSelectedWorkersOnScheduleBlockDay(block, aspects, day, workerIds) {
  if (!workerIds?.length || !block || !day) {
    return 0;
  }
  const targetIds = new Set(
    workerIds.map((id) => Number(id)).filter((id) => Number.isFinite(id))
  );
  if (!targetIds.size) {
    return 0;
  }

  const aspect = getScheduleAspectForBlockOnDate(aspects, block.id, day);
  const assignment = aspect
    ? aspect.worker_assignment ?? []
    : block.worker_assignment ?? [];
  return assignment.reduce(
    (count, worker) => (targetIds.has(Number(worker?.id)) ? count + 1 : count),
    0
  );
}

/**
 * @param {Array<Object>} blocks
 * @param {Array<Object>} aspects
 * @param {Array<Date>} visibleDays
 * @param {Array<string>} workScopeIds
 * @param {Array<string>} workerIds
 * @returns {Array<Object>}
 */
export function filterScheduleBlocksForToolbar(
  blocks,
  aspects,
  visibleDays,
  workScopeIds,
  workerIds
) {
  let list = blocks ?? [];
  if (workScopeIds?.length) {
    list = list.filter((block) => blockMatchesWorkScopeFilter(block, workScopeIds));
  }
  if (workerIds?.length) {
    list = list.filter((block) =>
      blockMatchesWorkerFilter(block, aspects, visibleDays, workerIds)
    );
  }
  return list;
}

/**
 * Search-match a schedule block by the scope name shown on the bar.
 *
 * @param {Object} block
 * @param {string} query Raw search text.
 * @returns {boolean}
 */
export function scheduleBlockMatchesScopeSearch(block, query) {
  return itemMatchesSearchQuery(block, query, ["scope.name"]);
}

/**
 * Keep only scope blocks whose scope name matches the active search text.
 *
 * @param {Array<Object>} blocks
 * @param {string} query Raw search text.
 * @returns {Array<Object>}
 */
export function filterScheduleBlocksForSearch(blocks, query) {
  if (!query?.trim()) {
    return blocks ?? [];
  }
  return (blocks ?? []).filter((block) =>
    scheduleBlockMatchesScopeSearch(block, query)
  );
}

/**
 * Filter the already-visible flat Schedule rows by project/group label or by a
 * matching visible scope block owned by the row.
 *
 * @param {Object} params
 * @param {Array<Object>} params.rows Flat visible Schedule rows.
 * @param {Array<Object>} params.projects Visible projects for project-name matches.
 * @param {Array<Object>} params.blocks Visible scope blocks after toolbar filters.
 * @param {string} params.query Raw search text.
 * @returns {Array<Object>}
 */
export function filterScheduleRowsForSearch({ rows, projects, blocks, query }) {
  if (!query?.trim()) {
    return rows ?? [];
  }

  const projectById = new Map(
    (projects ?? []).map((project) => [project.id, project])
  );
  const rowIdsWithMatchingScopeBlocks = new Set(
    (blocks ?? [])
      .filter((block) => scheduleBlockMatchesScopeSearch(block, query))
      .map((block) => getScheduleBlockRowId(block))
  );

  return (rows ?? []).filter((row) => {
    const project = projectById.get(row.projectId);
    return (
      itemMatchesSearchQuery(row, query, ["label"]) ||
      itemMatchesSearchQuery(project, query, ["projectNumber", "name"]) ||
      rowIdsWithMatchingScopeBlocks.has(row.id)
    );
  });
}
